import {
  type DescService,
  hasOption,
  type JsonObject,
} from "@bufbuild/protobuf";
import type { Interceptor } from "@connectrpc/connect";
import { type Client, createClient, type Transport } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { RecoveryClient } from "#root/bin/recoveryserver/client";
import { BaseService } from "#root/proto/base_pb.js";
import { XXX_THINK_CAREFULLY_no_token } from "#root/proto/options_pb.js";
import { UserService } from "#root/proto/user_pb.js";

export class VaultClient {
  protected flagsFetched: Promise<void>;
  protected baseURL: string;
  protected credentials: Credentials;
  protected transport: Transport;

  private cachedFlags: Map<string, string>;

  constructor(baseURL = "") {
    this.baseURL = baseURL;
    this.transport = createConnectTransport({
      baseUrl: baseURL,
      interceptors: [this.authInterceptor],
    });

    // Always fetch the current flags at startup so that derived classes can use them.
    const flagsClient = this.useClient(BaseService);
    this.flagsFetched = flagsClient.getFlags({}).then((value) => {
      this.cachedFlags = new Map(Object.entries(value.flags));
    });
  }

  /**
   * @returns a client with the correct base URL and any additional credentials.
   */
  private useClient<T extends DescService>(service: T): Client<T> {
    return createClient(service, this.transport);
  }

  /**
   * @returns an interceptor that can automatically add token fetching to requests.
   */
  private authInterceptor: Interceptor = (next) => async (req) => {
    if (!hasOption(req.method, XXX_THINK_CAREFULLY_no_token)) {
      // XXX: if BaseService.GetToken does not have XXX_THINK_CAREFULLY_no_token, this will make an infinite loop. :)
      const newToken = await this.getToken(req.method.name);
      req.header.set("Callisto-Token", newToken);
      req.header.set("X-Callisto-Token", newToken);
    }
    return await next(req);
  };

  // Configuration Flags ///////////////////////////////////////////////////////
  public async getFlag(key: string): Promise<string> {
    await this.flagsFetched;
    return this.cachedFlags.get(key);
  }

  // Account Recovery //////////////////////////////////////////////////////////
  public async getRecoveryClient(): Promise<RecoveryClient> {
    return new RecoveryClient(
      await this.getFlag("UI_ENV_RECOVERY_ALPHA_URL"),
      await this.getFlag("UI_ENV_RECOVERY_BETA_URL"),
    );
  }

  // Account Management ////////////////////////////////////////////////////////
  public get isLoggedIn(): boolean {
    return this.credentials !== undefined;
  }

  protected setCredentials(c: Credentials) {
    this.credentials = c;
  }

  protected destroyCredentials() {
    this.credentials = undefined;
  }

  async logout(): Promise<void> {
    // Calling the service to delete the CALLISTO_AUTH token is now necessary, since client-side JS can't do it.
    const client = this.useClient(UserService);
    await client.logout({});

    this.destroyCredentials();
  }

  // Tokens ////////////////////////////////////////////////////////////////////
  async getToken(operation?: string): Promise<string> {
    const client = this.useClient(BaseService);
    const resp = await client.getToken({ operation });

    return resp.token;
  }

  // Surveys ///////////////////////////////////////////////////////////////////
  async submitSurvey(
    pk: Uint8Array,
    input: JsonObject,
    kind: string,
    linkedCampus: string,
    linkedEntry: string,
  ): Promise<string> {
    const client = this.useClient(UserService);
    const resp = await client.respondToSurvey({
      pk,
      responses: input,
      kind,
      linkedCampus,
      linkedEntry,
    });

    return resp.surveyID;
  }
}

export interface Credentials {
  readonly pk: Uint8Array;

  encrypt<T>(plaintext: T): Uint8Array;
  decrypt<T>(ciphertext: Uint8Array): T;

  sign(plaintext: Uint8Array): Uint8Array;
  verify(signature: Uint8Array): boolean;
}

export class InMemoryCredentials implements Credentials {
  private readonly sk: Uint8Array;
  readonly pk: Uint8Array;

  constructor(sk: Uint8Array, pk: Uint8Array) {
    this.pk = pk;
    this.sk = sk;
  }

  encrypt<T>(plaintext: T): Uint8Array {
    return new Uint8Array();
  }
  decrypt<T>(ciphertext: Uint8Array): T {
    return {} as T;
  }

  sign(plaintext: Uint8Array): Uint8Array {
    return new Uint8Array();
  }
  verify(signature: Uint8Array): boolean {
    return true;
  }
}
